<template>
    <div v-if="form" class="form w-full h-full flex flex-col relative md:px-6 py-16 sm:px-6 pb-8"
        style="max-width: 900px">
        <div v-if="form_stage !== 'postcode'"
            class="back-btn font-bold absolute left-0 top-2 sm:left-5 sm:top-5 p-3 cursor-pointer" @click="goBack">
            <svg version="1.2" preserveAspectRatio="none" viewBox="0 0 24 24" style="
          display: inline-block;
          opacity: 1;
          mix-blend-mode: normal;
          width: 32px;
          height: 32px;
          transform: translateY(-1px);
        " class="back-button-svg">
                <g>
                    <path xmlns:default="http://www.w3.org/2000/svg"
                        d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" style="fill: currentColor">
                    </path>
                </g>
            </svg>
            Back
        </div>
        <!-- POSTCODE -->
        <form-page v-if="form_stage === 'postcode'">
            <h1>What's your postcode?</h1>
            <input type="text" name="postcode" v-model="form.post_code" placeholder="E.g., LS1 3ED"
                @input="messages['postcode'] = []" />
            <button @click="next(submitPostcode)">Start Quote</button>
            <div id="mmWrapper"></div>
        </form-page>

        <!-- FUEL TYPE -->
        <form-page v-if="form_stage === 'fuel_type'">
            <h1>Which fuels would you like to compare?</h1>
            <icon-chooser :options="options.fuel_type" @Select="
                (type) => {
                    $set(form, 'fuel_type', type);
                    messages['fuel_type'] = [];
                }
            " :selected="form.fuel_type" class="mb-6" key="icon-chooser-fuel-type" />
            <custom-checkbox id="same-supplier" v-if="both_fuels" v-model="form.isDualFuel" class="mt-8">
                I have the same supplier for electricity and gas
            </custom-checkbox>
            <button @click="next(submitFuelType)">Continue</button>
        </form-page>

        <!-- METER TYPE -->
        <form-page v-if="form_stage === 'meter_type'">
            <tool-tip-alt text="Unsure which type of meter you have?">
                <div class="px-8 flex flex-col font-semibold">
                    <h1 class="mb-2" style="padding-bottom: 0px">
                        Unsure which type of meter you have?
                    </h1>

                    <span class="mb-6">Our handy guide below will help you determine which type of meter
                        you currently have:</span>

                    <ul class="mx-auto text-left">
                        <li class="mb-3">
                            <strong>Traditional Meter:</strong> These meters show the units of
                            energy you use on a simple, mechanical display.
                        </li>
                        <li class="mb-3">
                            <strong>Economy 7:</strong> If you are on an economy 7 tariff,
                            which charge lower rates of electricity during the night then you
                            will have this meter. It also displays two readings one for night
                            and one for day.
                        </li>
                        <li class="mb-3">
                            <strong>Smart Meter:</strong> These are digital meters, and you
                            should see a row of numbers displayed on an LCD screen. You may
                            need to press a button for the meter to display the reading.
                        </li>
                    </ul>
                </div>
            </tool-tip-alt>

            <h1>Which type of meters do you currently have?</h1>
            <icon-chooser :options="options.meter_type" @Select="
                (type) => {
                    $set(form, 'meter_type', type);
                }
            " :selected="form.meter_type" class="mb-6" key="icon-chooser-meter-type" />
            <div class="flex-row mt-4" v-if="form.meter_type && form.meter_type.value === 'economy_seven'">
                <span class="font-semibold mb-1 mr-4 inline-block text-right" style="min-width: 230px">
                    My current Economy 7 percentage is:
                </span>
                <input type="text" v-model="form.economy7Percentage" class="mb-4" :class="{
                    'text-red': !(
                        0 <= parseInt(form.economy7Percentage) &&
                        parseInt(form.economy7Percentage) <= 100
                    ),
                }" style="max-width: 100px" />
                <span class="relative -left-9 font-bold overflow-visible inline-block" style="width: 0">%</span>
            </div>

            <custom-checkbox v-if="form.meter_type && form.meter_type.value != 'S1'" id="want-smart-meter"
                v-model="form.interestedElectricSmartMeter" class="mt-4">
                I am interested in having a smart meter installed
            </custom-checkbox>
            <button @click="next(submitMeterType)">Continue</button>
        </form-page>

        <!-- PAYMENT TYPE -->
        <form-page v-if="form_stage === 'payment_type'">
            <h1>Do you currently pay by a prepayment meter?</h1>
            <div class="text-button" :class="{ selected: form.prepayment_meter }" @click="
                () => {
                    $set(form, 'prepayment_meter', true);
                }
            ">
                Yes
                <div v-if="form.prepayment_meter" class="relative">
                    <div class="bg-orange cbox-wrapper" style="width: 25px; height: 25px">
                        <span class="checkmark" />
                    </div>
                </div>
            </div>
            <div class="text-button" :class="{ selected: form.prepayment_meter === false }" @click="
                () => {
                    $set(form, 'prepayment_meter', false);
                }
            ">
                No
                <div v-if="form.prepayment_meter === false" class="relative">
                    <div class="bg-orange cbox-wrapper" style="width: 25px; height: 25px">
                        <span class="checkmark" />
                    </div>
                </div>
            </div>
            <div class="messages font-semibold px-8" v-if="form.prepayment_meter === false">
                We're sorry, you must have a prepayment meter to continue with your
                quotation.
            </div>

            <button @click="next(submitPaymentType)">Continue</button>
        </form-page>

        <!-- GAS SUPPLIER -->
        <form-page v-if="form_stage === 'gas_supplier'">
            <tool-tip-alt text="Unsure which supplier you're with?">
                <div class="px-8 flex flex-col font-semibold">
                    <h1 class="mb-2" style="padding-bottom: 0px">
                        Unsure who your current supplier is?
                    </h1>

                    <span class="mb-6">
                        To find out your current supplier, the best place is to look at your
                        most recent bill. If you don’t have a recent bill available, then
                        the below could help:
                    </span>

                    <ul class="mx-auto text-left">
                        <li class="mb-3">
                            To find your gas supplier, you can use the ‘Find My Supplier’
                            service www.findmysupplier.energy or alternatively, call the Meter
                            Number helpline on 0870 608 1524.
                        </li>
                        <li class="mb-3">
                            To find your electricity supplier, you can call your Regional
                            Distribution telephone number. All regions and telephone numbers
                            are listed below:
                            <ul class="font-normal px-10 mt-2">
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>North Scotland</span>
                                    <span class="font-semibold">0345 026 2554</span>
                                </li>
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>Central and South Scotland</span>
                                    <span class="font-semibold">0330 101 0300</span>
                                </li>
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>North East England & Yorkshire</span>
                                    <span class="font-semibold">0800 0111 3332</span>
                                </li>
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>North West England</span>
                                    <span class="font-semibold">0800 195 4141</span>
                                </li>
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>London, South East England & Eastern England</span>
                                    <span class="font-semibold">0800 029 4285</span>
                                </li>
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>Central Southern England</span>
                                    <span class="font-semibold">0345 026 2554</span>
                                </li>
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>South West England & South Wales</span>
                                    <span class="font-semibold">0800 096 3080</span>
                                </li>
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>East Midlands & West Midlands</span>
                                    <span class="font-semibold">0800 096 3080</span>
                                </li>
                                <li class="flex justify-between flex-wrap mb-1">
                                    <span>Merseyside, Cheshire, North Shropshire & North Wales</span>
                                    <span class="font-semibold">0330 101 0300</span>
                                </li>
                            </ul>
                        </li>
                    </ul>
                    Please note that the cost of calling a phone number depends on the
                    digits it starts with, your phone provider and whether you use a
                    landline or mobile. However, it is now free for mobile users to call
                    0800 and 0808 numbers.
                </div>
            </tool-tip-alt>

            <h1>Who is your current gas supplier?</h1>

            <select v-if="form_state.show_other_suppliers" v-model="form.gas_supplier">
                <option :value="null" default>Please Select</option>
                <option v-for="supplier in all_suppliers" :value="supplier" :key="`supplier-${supplier.id}`">
                    {{ supplier.name }}
                </option>
            </select>
            <button @click="next(submitGasSupplier)">Continue</button>
        </form-page>

        <!-- GAS TARIFF -->
        <form-page v-if="form_stage === 'gas_tariff'">
            <tool-tip-alt text="How to find your tariff name">
                <div class="px-8 flex flex-col font-semibold">
                    <h1 class="mb-2" style="padding-bottom: 0px">
                        How to find your tariff name
                    </h1>

                    <span class="mb-6">
                        The name of your tariff should appear on your most recent bill under
                        'plan' or 'tariff'.
                    </span>
                </div>
            </tool-tip-alt>
            <h1>Which gas tariff are you currently on?</h1>
            <select v-model="form.gas_tariff">
                <option :value="null" default>Please Select</option>
                <option v-for="tariff in gas_tariffs" :key="`gas-tariff-option-${tariff.id}`" :value="tariff">
                    {{ tariff.name }}
                </option>
            </select>

            <button @click="next(submitGasTariff)">Continue</button>
        </form-page>

        <!-- ELECTRICITY SUPPLIER -->
        <form-page v-if="form_stage === 'electric_supplier'">
            <tool-tip-alt text="Unsure which supplier you're with?">
                <div class="px-8 flex flex-col font-semibold">
                    <h1 class="mb-2" style="padding-bottom: 0px">
                        Unsure who your current supplier is?
                    </h1>

                    <span class="mb-6">
                        To find out your current supplier, the best place is to look at your
                        most recent bill. If you don’t have a recent bill available, then
                        the below could help:
                    </span>

                    <ul class="mx-auto text-left">
                        <li class="mb-3">
                            Gas Supplier: Call the Meter Point Helpline on 0870 608 1524 (call
                            charges may apply) or
                            <a class="text-orange" target="_blank"
                                href="https://www.findmysupplier.energy/webapp/index.html">click here</a>.
                        </li>
                        <li class="mb-3">
                            Electricity Supplier: To find your electricity supplier please
                            <a class="text-orange" target="_blank"
                                href="https:/www.energynetworks.org/operating-the-networks/whos-my-network-operator">click
                                here</a>.
                        </li>
                    </ul>
                </div>
            </tool-tip-alt>
            <h1>Who is your current electricity supplier?</h1>

            <select v-if="form_state.show_other_suppliers" v-model="form.electricity_supplier">
                <option :value="null" default>Please Select</option>
                <option v-for="supplier in all_suppliers" :value="supplier" :key="`supplier-${supplier.id}`">
                    {{ supplier.name }}
                </option>
            </select>
            <button @click="next(submitElectricSupplier)">Continue</button>
        </form-page>

        <!-- ELECTRICITY TARIFF -->
        <form-page v-if="form_stage === 'electric_tariff'">
            <tool-tip-alt text="How to find your tariff name">
                <div class="px-8 flex flex-col font-semibold">
                    <h1 class="mb-2" style="padding-bottom: 0px">
                        How to find your tariff name
                    </h1>

                    <span class="mb-6">
                        The name of your tariff should appear on your most recent bill under
                        'plan' or 'tariff'.
                    </span>
                </div>
            </tool-tip-alt>
            <h1>Which electricity tariff are you currently on?</h1>
            <select v-model="form.electricity_tariff">
                <option :value="null" default>Please Select</option>
                <option v-for="tariff in electric_tariffs" :key="`electric-tariff-option-${tariff.id}`" :value="tariff">
                    {{ tariff.name }}
                </option>
            </select>

            <button @click="next(submitElectricTariff)">Continue</button>
        </form-page>

        <!-- DUAL SUPPLIER -->
        <form-page v-if="form_stage === 'dual_supplier'">
            <h1>Who is your current supplier?</h1>
            <tool-tip-alt text="Unsure which supplier you're with?">
                <div class="px-8 flex flex-col font-semibold">
                    <h1 class="mb-2" style="padding-bottom: 0px">
                        Unsure who your current supplier is?
                    </h1>

                    <span class="mb-6">
                        To find out your current supplier, the best place is to look at your
                        most recent bill. If you don’t have a recent bill available, then
                        the below could help:
                    </span>

                    <ul class="mx-auto text-left">
                        <li class="mb-3">
                            Gas Supplier: Call the Meter Point Helpline on 0870 608 1524 (call
                            charges may apply) or
                            <a class="text-orange" target="_blank"
                                href="https://www.findmysupplier.energy/webapp/index.html">click here</a>.
                        </li>
                        <li class="mb-3">
                            Electricity Supplier: To find your electricity supplier please
                            <a class="text-orange" target="_blank"
                                href="https:/www.energynetworks.org/operating-the-networks/whos-my-network-operator">click
                                here</a>.
                        </li>
                    </ul>
                </div>
            </tool-tip-alt>
            <icon-chooser :options="featured_supplier_options" :selected="form.dual_supplier" class="mb-6" @Select="
                (type) => {
                    $set(form_state, 'show_other_suppliers', false);
                    $set(form, 'dual_supplier', type);
                }
            " key="icon-chooser-dual-supplier" />

            <select v-if="form_state.show_other_suppliers" v-model="form.dual_supplier">
                <option :value="null" default>Please Select</option>
                <option v-for="supplier in all_suppliers" :value="supplier" :key="`supplier-${supplier.id}`">
                    {{ supplier.name }}
                </option>
            </select>
            <button @click="next(submitDualSupplier)">Continue</button>
        </form-page>

        <!-- DUAL TARIFF -->
        <form-page v-if="form_stage === 'dual_tariff'">
            <tool-tip-alt text="How to find your tariff name">
                <div class="px-8 flex flex-col font-semibold">
                    <h1 class="mb-2" style="padding-bottom: 0px">
                        How to find your tariff name
                    </h1>

                    <span class="mb-6">
                        The name of your tariff should appear on your most recent bill under
                        'plan' or 'tariff'.
                    </span>
                </div>
            </tool-tip-alt>
            <h1>Which dual tariff are you currently on?</h1>
            <select v-model="form.dual_tariff">
                <option :value="null" default>Please Select</option>
                <option v-for="tariff in dual_tariffs" :key="`dual-tariff-option-${tariff.id}`" :value="tariff">
                    {{ tariff.name }}
                </option>
            </select>

            <button @click="next(submitDualTariff)">Continue</button>
        </form-page>

        <!-- YOUR USAGE -->
        <form-page v-if="form_stage === 'usage'">
            <h1>How much energy do you use?</h1>
            <div class="radio-group flex flex-col items-start mb-4">
                <input id="usage-pounds" type="radio" name="usage-type" v-model="form.calculationType" value="Spend" />
                <label for="usage-pounds">I know how much I spend in £'s</label>
                <input id="usage-kwh" type="radio" name="usage-type" v-model="form.calculationType" value="KWH" />
                <label for="usage-kwh">I know how much I use in kWh</label>
                <input id="usage-unsure" type="radio" name="usage-type" v-model="form.calculationType"
                    value="HouseSize" />
                <label for="usage-unsure">I'm unsure</label>
            </div>
            <template v-if="
                form.calculationType === 'Spend' || form.calculationType === 'KWH'
            ">
                <div class="flex flex-col w-full items-center">
                    <div v-if="['Dual', 'Gas Only'].includes(form.fuel_type.value)" class="flex-row w-full">
                        <span class="font-semibold mb-1 mr-4 inline-block text-center sm:text-right"
                            style="min-width: 230px">
                            My current gas
                            {{ form.calculationType === "Spend" ? "spend" : "usage" }} is:
                        </span>
                        <div class="block sm:inline">
                            <input style="max-width: 150px" :class="{
                                [form.calculationType === 'Spend' ? 'currency' : 'kwh']: true,
                            }" class="mb-4" type="text" v-model="form.gasUsage" />
                            <span v-if="form.calculationType === 'KWH'"
                                class="relative -left-12 font-bold overflow-visible inline-block"
                                style="width: 0">kWh</span>
                            <span class="font-semibold mx-4">Each</span>
                            <select style="max-width: 140px" v-model="form.gas_usage_period">
                                <option value="Week">Week</option>
                                <option value="Month">Month</option>
                                <option value="Quarter">Quarter</option>
                                <option value="Year" default>Year</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col w-full items-center">
                    <div v-if="['Dual', 'Electricity Only'].includes(form.fuel_type.value)" class="flex-row w-full">
                        <span class="font-semibold mb-1 mr-4 inline-block text-center sm:text-right"
                            style="min-width: 230px">
                            My current electricity
                            {{ form.calculationType === "Spend" ? "spend" : "usage" }} is:
                        </span>
                        <div class="block sm:inline">
                            <input style="max-width: 150px" :class="{
                                [form.calculationType === 'Spend' ? 'currency' : 'kwh']: true,
                            }" type="text" v-model="form.electricityUsage" />
                            <span v-if="form.calculationType === 'KWH'"
                                class="relative -left-12 font-bold overflow-visible inline-block"
                                style="width: 0">kWh</span>
                            <span class="font-semibold mx-4">Each</span>

                            <select style="max-width: 140px" v-model="form.electricity_usage_period">
                                <option value="Week">Week</option>
                                <option value="Month">Month</option>
                                <option value="Quarter">Quarter</option>
                                <option value="Year" default>Year</option>
                            </select>
                        </div>
                    </div>
                </div>
            </template>

            <template v-else-if="form.calculationType === 'HouseSize'">
                <icon-chooser :options="options.house_spend" :selected="form.usage" class="mb-6" @Select="
                    (option) => {
                        $set(form, 'usage', option.value);
                    }
                " key="icon-chooser-house-usage" />
            </template>

            <button @click="next(submitUsage)">Get my personal quote</button>
        </form-page>

        <!-- YOUR QUOTE -->
        <form-page v-if="form_stage === 'quote'">
            <h1 v-if="
                quote &&
                quote.gasQuoteTariff &&
                quote.newGasQuoteTariff &&
                quote_positive
            " class="text-medium" style="max-width: 500px">
                You can save by switching to E!
            </h1>
            <h1 v-else-if="quote_positive === false">
                You can join E for the following benefits.
            </h1>
            <img src="e_logo.png" width="70" height="auto" class="mb-4" style="width: 70px; height: auto" />
            <div class="relative pb-12 font-semibold px-4">
                {{
                    quote_positive
                        ? "And don't forget about all our other great benefits such as:"
                        : ""
                }}

                <div
                    class="mt-4 flex flex-row w-auto xs:w-full justify-center items-start xs:relative left-5 flex-wrap w-100 benefits">
                    <template v-for="(benefit, index) in benefits">
                        <div v-if="
                            (index !== 3 && index !== 4) || form.fuel_type.value === 'Dual'
                        " class="benefit flex-row flex justify-start xs:justify-start items-center pl-10 xs:pl-0"
                            :key="`benefits-${index}`">
                            <svg version="1.2" preserveAspectRatio="none" viewBox="0 0 24 24" class="inline-block"
                                data-id="ed3a8969e03f441bb4d5f48826eca480" style="
                  opacity: 1;
                  mix-blend-mode: normal;
                  fill: rgb(29, 184, 127);
                  width: 29px;
                  height: 29px;
                  transform: rotate(0deg) translate(-4px, 2px);
                ">
                                <g>
                                    <path xmlns:default="http://www.w3.org/2000/svg" id="ok"
                                        d="M20.05,8.23c0-0.27-0.1-0.52-0.29-0.71l-1.42-1.41c-0.39-0.39-1.02-0.39-1.41-0.01c0,0,0,0-0.01,0.01l-6.83,6.84  L7.03,9.88C6.64,9.49,6.01,9.49,5.62,9.87c0,0,0,0-0.01,0.01L4.2,11.29c-0.39,0.39-0.39,1.02-0.01,1.41c0,0,0,0,0.01,0.01l5.18,5.18  c0.39,0.39,1.02,0.39,1.41,0.01c0,0,0,0,0.01-0.01l9-9c0.19-0.19,0.29-0.44,0.29-0.71l0,0L20.05,8.23z"
                                        style="fill: rgb(29, 184, 127)"></path>
                                </g>
                            </svg>
                            <span class="pl-2 xs:pl-0 text-left">{{ benefit }}</span>
                        </div>
                    </template>
                    <div v-if="form.meter_type.value == 'S1'"
                        class="benefit flex-row flex justify-start xs:justify-start items-center pl-10 xs:pl-0">
                        <svg version="1.2" preserveAspectRatio="none" viewBox="0 0 24 24" class="inline-block"
                            data-id="ed3a8969e03f441bb4d5f48826eca480" style="
                opacity: 1;
                mix-blend-mode: normal;
                fill: rgb(29, 184, 127);
                width: 29px;
                height: 29px;
                transform: rotate(0deg) translate(-4px, 2px);
              ">
                            <g>
                                <path xmlns:default="http://www.w3.org/2000/svg" id="ok"
                                    d="M20.05,8.23c0-0.27-0.1-0.52-0.29-0.71l-1.42-1.41c-0.39-0.39-1.02-0.39-1.41-0.01c0,0,0,0-0.01,0.01l-6.83,6.84  L7.03,9.88C6.64,9.49,6.01,9.49,5.62,9.87c0,0,0,0-0.01,0.01L4.2,11.29c-0.39,0.39-0.39,1.02-0.01,1.41c0,0,0,0,0.01,0.01l5.18,5.18  c0.39,0.39,1.02,0.39,1.41,0.01c0,0,0,0,0.01-0.01l9-9c0.19-0.19,0.29-0.44,0.29-0.71l0,0L20.05,8.23z"
                                    style="fill: rgb(29, 184, 127)"></path>
                            </g>
                        </svg>
                        <span class="pl-2 xs:pl-0 text-left">No Standing Charge</span>
                    </div>
                    <div v-if="
                        form.meter_type.value == 'S1' ||
                        form.meter_type.value == 'traditional'
                    " class="benefit flex-row flex justify-start xs:justify-start items-center pl-10 xs:pl-0">
                        <svg version="1.2" preserveAspectRatio="none" viewBox="0 0 24 24" class="inline-block"
                            data-id="ed3a8969e03f441bb4d5f48826eca480" style="
                opacity: 1;
                mix-blend-mode: normal;
                fill: rgb(29, 184, 127);
                width: 29px;
                height: 29px;
                transform: rotate(0deg) translate(-4px, 2px);
              ">
                            <g>
                                <path xmlns:default="http://www.w3.org/2000/svg" id="ok"
                                    d="M20.05,8.23c0-0.27-0.1-0.52-0.29-0.71l-1.42-1.41c-0.39-0.39-1.02-0.39-1.41-0.01c0,0,0,0-0.01,0.01l-6.83,6.84  L7.03,9.88C6.64,9.49,6.01,9.49,5.62,9.87c0,0,0,0-0.01,0.01L4.2,11.29c-0.39,0.39-0.39,1.02-0.01,1.41c0,0,0,0,0.01,0.01l5.18,5.18  c0.39,0.39,1.02,0.39,1.41,0.01c0,0,0,0,0.01-0.01l9-9c0.19-0.19,0.29-0.44,0.29-0.71l0,0L20.05,8.23z"
                                    style="fill: rgb(29, 184, 127)"></path>
                            </g>
                        </svg>
                        <span class="pl-2 xs:pl-0 text-left">Award Winning Customer Service</span>
                    </div>
                </div>
            </div>

            <div class="w-full px-8" v-if="quote && quote.gasQuoteTariff && quote.newGasQuoteTariff"
                style="max-width: 500px">
                <table v-if="
                    !(
                        quote.type == 'Electricity Only' &&
                        quote.newElectricityQuoteTariff.isEco7 == true
                    )
                ">
                    <tr>
                        <th></th>
                        <th style="width: 200px" class="relative"
                            v-if="quote.type == 'Dual' || quote.type !== 'Electricity Only'">
                            Gas
                        </th>
                        <th style="width: 200px" v-if="
                            (quote.type == 'Dual' || quote.type !== 'Gas Only') &&
                            quote.newElectricityQuoteTariff.isEco7 == false
                        ">
                            Electricity
                        </th>
                    </tr>

                    <tr v-if="should_show_standing_charge">
                        <th>Standing Charge</th>
                        <td v-if="quote.type == 'Dual' || quote.type == 'Gas Only'">
                            {{
                                (
                                    quote.newGasQuoteTariff.tariff.gas_standing_charge * 100
                                ).toFixed(2)
                            }}p/day
                        </td>
                        <td v-if="
                            (quote.type == 'Dual' || quote.type == 'Electricity Only') &&
                            quote.newElectricityQuoteTariff.isEco7 == false
                        ">
                            {{
                                (
                                    quote.newElectricityQuoteTariff.tariff
                                        .electric_standing_charge * 100
                                ).toFixed(2)
                            }}p/day
                        </td>
                    </tr>

                    <tr v-if="should_show_unit_rate">
                        <th>Unit Rate</th>
                        <td v-if="quote.type == 'Dual' || quote.type == 'Gas Only'">
                            {{
                                (quote.newGasQuoteTariff.tariff.gas_kwh * 100).toFixed(2)
                            }}p/kWh
                        </td>
                        <td v-if="
                            (quote.type == 'Dual' || quote.type == 'Electricity Only') &&
                            quote.newElectricityQuoteTariff.isEco7 == false
                        ">
                            {{
                                (
                                    quote.newElectricityQuoteTariff.tariff.electric_kwh * 100
                                ).toFixed(2)
                            }}p/kWh
                        </td>
                    </tr>

                    <tr v-if="should_show_unit_rate_1">
                        <th>Unit Rate 1</th>
                        <td v-if="quote.type == 'Dual' || quote.type == 'Gas Only'">
                            {{
                                (quote.newGasQuoteTariff.tariff.s1_gas_unit1_kwh * 100).toFixed(
                                    2,
                                )
                            }}p/kWh
                        </td>
                        <td v-if="
                            (quote.type == 'Dual' || quote.type == 'Electricity Only') &&
                            quote.newElectricityQuoteTariff.isEco7 == false
                        ">
                            {{
                                (
                                    quote.newElectricityQuoteTariff.tariff.s1_electric_unit1_kwh *
                                    100
                                ).toFixed(2)
                            }}p/kWh
                        </td>
                    </tr>

                    <tr v-if="should_show_unit_rate_2">
                        <th>Unit Rate 2</th>
                        <td v-if="quote.type == 'Dual' || quote.type == 'Gas Only'">
                            {{
                                (quote.newGasQuoteTariff.tariff.s1_gas_unit2_kwh * 100).toFixed(
                                    2,
                                )
                            }}p/kWh
                        </td>
                        <td v-if="
                            (quote.type == 'Dual' || quote.type == 'Electricity Only') &&
                            quote.newElectricityQuoteTariff.isEco7 == false
                        ">
                            {{
                                (
                                    quote.newElectricityQuoteTariff.tariff.s1_electric_unit2_kwh *
                                    100
                                ).toFixed(2)
                            }}p/kWh
                        </td>
                    </tr>

                    <tr>
                        <th>Annual Spend</th>
                        <td v-if="quote.type == 'Dual' || quote.type == 'Gas Only'">
                            £{{ quote.newGasQuoteTariff.annualSpend.toFixed(2) }}
                        </td>
                        <td v-if="
                            (quote.type == 'Dual' || quote.type == 'Electricity Only') &&
                            quote.newElectricityQuoteTariff.isEco7 == false
                        ">
                            £{{ quote.newElectricityQuoteTariff.annualSpend.toFixed(2) }}
                        </td>
                    </tr>
                </table>

                <table v-if="quote.newElectricityQuoteTariff.isEco7 == true">
                    <tr>
                        <th></th>
                        <th style="width: 200px" v-if="
                            (quote.type == 'Dual' || quote.type !== 'Gas Only') &&
                            quote.newElectricityQuoteTariff.isEco7 == true
                        ">
                            Electricity
                        </th>
                    </tr>
                    <tr>
                        <th>Standing Charge</th>
                        <td>
                            {{
                                (
                                    quote.newElectricityQuoteTariff.eco7StandingCharge * 100
                                ).toFixed(2)
                            }}p/day
                        </td>
                    </tr>
                    <tr>
                        <th>Day Rate</th>
                        <td>
                            {{
                                (
                                    quote.newElectricityQuoteTariff.eco7DayCostPerKWH * 100
                                ).toFixed(3)
                            }}p/kWh
                        </td>
                    </tr>
                    <tr>
                        <th>Night Rate</th>
                        <td>
                            {{
                                (
                                    quote.newElectricityQuoteTariff.eco7NightCostPerKWH * 100
                                ).toFixed(3)
                            }}p/kWh
                        </td>
                    </tr>
                    <tr>
                        <th>Annual Spend</th>
                        <td>
                            £{{ quote.newElectricityQuoteTariff.annualSpend.toFixed(2) }}
                        </td>
                    </tr>
                </table>
            </div>

            <div class="flex flex-col justify-start py-5 px-3" v-if="form.meter_type.value == 'S1'">
                <span class="text-left text-sm font-medium">
                    Unit rate 1 is collected for the first 2kWh of gas and 2 kWh of
                    electricity used per day.
                </span>
                <span class="text-left text-sm font-medium">
                    Unit rate 2 is for all subsequent usage.
                </span>
            </div>

            <button @click="next(switchToE)">Switch to E</button>
        </form-page>

        <!-- SWITCH TO US -->
        <form-page v-if="form_stage === 'switch_to_us'">
            <h1>
                You're almost there, we just need to know a little bit about you to get
                you switched to us.
            </h1>
            <div class="flex flex-row justify-between items-end w-full px-6 sm:w-3/5 mb-8">
                <select v-model="form.selected_address" class="select-address">
                    <option :value="null" default>Select your address</option>
                    <option v-for="(address, index) in options.addresses" :key="`address-option-${index}`"
                        :value="address">
                        {{address.filter((address) => address.length > 1).join(", ")}}
                    </option>
                </select>
            </div>
            <div class="flex flex-row justify-between items-end w-full px-6 sm:w-3/5 mb-8">
                <select v-model="form.title" autocomplete="honorific-prefix" class="underline-input mb-0"
                    style="width: 45%">
                    <option :value="null" default>Title</option>
                    <option value="Dr">Dr</option>
                    <option value="Miss">Miss</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Mx">Mx</option>
                    <option value="Prof">Prof</option>
                </select>
            </div>
            <div class="flex flex-row justify-between mb-4 w-full px-6 sm:w-3/5 mb-8">
                <input type="text" class="underline-input" autocomplete="given-name" style="width: 45%"
                    v-model="form.first_name" placeholder="First Name" />
                <input type="text" class="underline-input" style="width: 45%" autocomplete="family-name"
                    v-model="form.last_name" placeholder="Last Name" />
            </div>
            <div class="flex flex-row justify-between items-end w-full px-6 sm:w-3/5 mt-8 mb-8">
                <div class="flex flex-row justify-evenly date-of-birth items-end border-b-2 border-black"
                    style="width: 45%">
                    <input @input="changeDob('day')" ref="dob_day" type="text" style="width: 2rem" class="text-center"
                        v-model="form.age.day" maxLength="2" placeholder="DD" name="dd" />
                    /
                    <input @input="changeDob('month')" ref="dob_month" type="text" style="width: 2rem"
                        class="text-center" v-model="form.age.month" maxLength="2" placeholder="MM" name="mm" />
                    /
                    <input @input="changeDob('year')" ref="dob_year" type="text" style="width: 3rem" class="text-center"
                        v-model="form.age.year" maxLength="4" placeholder="YYYY" name="yy" />
                </div>
            </div>
            <div class="w-full px-6 sm:w-3/5 mb-8">
                <input type="text" class="underline-input w-full phone-icon" autocomplete="tel"
                    v-model="form.phone_number" placeholder="Phone Number" />
            </div>
            <div class="w-full px-6 sm:w-3/5 mb-8">
                <input type="text" class="underline-input w-full email-icon" v-model="form.email" autocomplete="email"
                    placeholder="Email" />
            </div>

            <span class="px-6 w-full sm:w-3/5 text-left">
                Are you happy to pay for your energy during your cooling off period?
            </span>

            <div class="radio-group flex flex-col text-left items-start mb-4 px-6 w-full sm:w-3/5">
                <input id="agree-consent-no" type="radio" name="agree-consent" v-model="form.consent_to_pay"
                    :value="true" @click="
                        () => {
                            $set(form, 'number_of_days', 10);
                            $set(form, 'consent_to_pay', true);
                        }
                    " />
                <label for="agree-consent-no" style="display: flex">No
                    <span class="text-normal consent-to-pay" style="margin-left: 45%">
                        (Receive your top-up device in time for your supply start date)
                    </span></label>
                <input id="agree-consent-yes" type="radio" name="agree-consent" v-model="form.consent_to_pay"
                    :value="false" @click="
                        () => {
                            $set(
                                form,
                                'number_of_days',
                                this.$store.state.number_of_days.data
                                    .ConsentToPayYesDefaultDays,
                            );
                            $set(form, 'consent_to_pay', false);
                        }
                    " />
                <label for="agree-consent-yes" style="display: flex">Yes
                    <span class="text-normal consent-to-pay" style="margin-left: 45%">
                        (Your top-up device may not arrive in time for your supply start
                        date)</span></label>
            </div>

            <div v-if="form.consent_to_pay === true"
                class="flex flex-row justify-between mb-4 w-full px-6 sm:w-3/5 mb-8">
                <span class="w-full sm:w-2/5 text-left"> Switch to E in </span>
                <span class="font-bold">10 working days</span>
            </div>
            <div v-if="form.consent_to_pay === false"
                class="flex flex-row justify-between mb-4 w-full px-6 sm:w-3/5 mb-8">
                <span class="w-full sm:w-2/5 text-left"> Switch to E in </span>
                <select v-model="form.number_of_days" class="underline-input mb-0 dd-number-of-days">
                    <option :value="null" default>Please Select</option>
                    <!-- <option v-for="ctpNum in this.$store.state.number_of_days.data.CTPNumberOfDaysDropDownOptions.split(
                        ',',
                    ).filter((ctpNum) => ctpNum > 4)" :value="ctpNum" :key="`{{ctpNum}}`">
                        {{ ctpNum }} working days
                    </option> -->
                    <option v-for="ctpNum in (
                        ($store.state.number_of_days && $store.state.number_of_days.data && $store.state.number_of_days.data.CTPNumberOfDaysDropDownOptions)
                            ? $store.state.number_of_days.data.CTPNumberOfDaysDropDownOptions.split(',').filter((ctpNum) => ctpNum > 4)
                            : ['5', '6', '7', '8', '9', '10']
                    )" :value="ctpNum" :key="`ctp-no-${ctpNum}`">
                        {{ ctpNum }} working days
                    </option>
                </select>
            </div>

            <div class="radio-group flex flex-col text-left items-start mb-4 px-6 w-full sm:w-3/5">
                <input type="checkbox" id="prepayment-confirmation" v-model="form.prepayment_Confirm"
                    name="prepayment-confirm" />
                <label for="prepayment-confirmation" class="text-normal">
                    I confirm that a prepayment meter is safe and suitable for me.
                </label>
            </div>

            <span class="px-6 w-full sm:w-3/5 text-left">
                To create your account and provide our service to you, we will store
                your personal data securely. Are you happy for us to use this
                information to contact you about our products, promotions and services?
            </span>
            <div class="radio-group flex flex-col text-left items-start mb-4 px-6 w-full sm:w-3/5">
                <input id="agree-contact-no" type="radio" name="agree-contact" v-model="form.agrees_to_communication"
                    :value="false" />
                <label for="agree-contact-no" class="text-normal">No, I do not want you to use my details.</label>
                <input id="agree-contact-yes" type="radio" name="agree-contact" v-model="form.agrees_to_communication"
                    :value="true" />
                <label for="agree-contact-yes" class="text-normal">Yes, I am happy for you to use my details.</label>
            </div>

            <button @click="next(OTPSent)">Next</button>
        </form-page>
        <form-page v-if="form_stage === 'verify_OTP'">
            <div class="w-full px-6 sm:w-3/5 mb-8">
                <h1>
                    We just need to confirm this is you. Please enter the passcode we have
                    sent to your mobile to complete your application.
                </h1>
                <input type="text" class="underline-input w-full phone-icon" placeholder="Enter passcode"
                    v-model="form.OTP" />
            </div>

            <button @click="next(submitSwitch)">Switch Now</button>
        </form-page>
        <!--DONE  -->
        <form-page v-if="form_stage === 'done'">
            <div v-if="$store.state.form_success" class="px-8 text-center font-semibold">
                <p>
                    Thank you {{ form.first_name }}! Your application has been sucessfully
                    submitted.
                </p>

                <p>
                    We will review your application and send a confirmation email to
                    confirm your switch to us.
                </p>
                <p>The Team at E.</p>
            </div>

            <div v-if="!form_stage === 'done'">Sorry, there was a problem</div>
        </form-page>

        <!-- MESSAGES -->
        <div class="messages text-red mt-4 px-6" v-if="messages[form_stage]">
            <span v-for="(message, index) in messages[form_stage]" :key="`message-${index}`">{{ message }}</span>
        </div>
        <!-- PROGRESS BAR -->
        <div class="progress-bar bg-grey w-full h-6 my-6 mt-8">
            <div class="bg-orange h-full actual-progress" :style="`width: ${form_progress}%;`">
                &nbsp;
            </div>
        </div>
    </div>
</template>
<script>
const POSTCODE_REGEX =
    /^([A-Z]{1,2}[0-9]{1,2}|[A-Z]{1,2}[0-9][A-Z])\s?([0-9][A-Z]{2})$/i;
var ip = "";
// ensure the correct order of these
const stages = [
    "postcode",
    "fuel_type",
    "meter_type",
    "economy_7",
    "payment_type",
    "gas_supplier",
    "electric_supplier",
    "gas_tariff",
    "electric_tariff",
    "dual_supplier",
    "dual_tariff",
    "usage",
    "quote",
    "switch_to_us",
    "verify_OTP",
    "done",
];

import { mapState } from "vuex";
import axios from "axios";
import dayjs from "dayjs";

const defaults = {
    supplier: {
        id: 0,
        name: "",
        logo: "",
        logo_retina: "",
        is_smart_meter_supported: 0,
    },

    payment_type: {
        id: 0,
        name: "Prepayment Meter",
        description: "",
    },

    tariff: {
        id: 0,
        name: "",
        supplier_id: 0,
        region_id: 0,
        electric_kwh: 0,
        gas_kwh: 0,
        meter_type: "",
        electric_standing_charge: 0,
        gas_standing_charge: 0,
        s1_electric_unit1_kwh: 0,
        s1_electric_unit2_kwh: 0,
        s1_gas_unit1_kwh: 0,
        s1_gas_unit2_kwh: 0,
        s1_electric_threshold: 0,
        s1_gas_threshold: 0,
        tariff_id: 0,
        created_at: "2021-04-06T05:44:27.574Z",
        deleted_at: "2021-04-06T05:44:27.574Z",
        description: "",
        dual_fuel_discount: 0,
        eco7_day_kwh: 0,
        eco7_discount: 0,
        eco7_night_kwh: 0,
        eco7_standing_charge: 0,
        electric: "0",
        ends: "2021-04-06T05:44:27.574Z",
        exit_fees: 0,
        gas: "0",
        price_guarantee_until: "2021-04-06T05:44:27.574Z",
        terms: "",
        updated_at: "2021-04-06T05:44:27.574Z",
    },
};

const url_prefix = "";
export default {
    props: [],
    data() {
        return {
            authToken: null,
            tokenExpiry: null,
            modal_open: false,
            form_stage: "postcode",
            form_state: {
                show_other_suppliers: true,
            },
            is_submitting: false,
            benefits: [
                "No Exit Fees",
                "Which? Recommended",
                "Free Smart Meters",
                "Dual Fuel Loyalty Credit",
            ],
            s1benefits: ["Award Winning Customer Service"],
            options: {
                fuel_type: [
                    {
                        value: "Dual",
                        label: "Gas & Electricity",
                        image: `${url_prefix}imgs/gas-electric.png`,
                    },
                    {
                        value: "Gas Only",
                        label: "Gas",
                        image: `${url_prefix}imgs/gas.png`,
                    },
                    {
                        value: "Electricity Only",
                        label: "Electricity",
                        image: `${url_prefix}imgs/electricity.png`,
                    },
                ],
                meter_type: [
                    {
                        image: `${url_prefix}imgs/smart_meter.png`,
                        value: "S1",
                        key: "smart_meter",
                        label: "Smart Electricity Meter",
                    },
                    {
                        image: `${url_prefix}imgs/traditional_meter.png`,
                        value: "traditional",
                        key: "traditional",
                        label: "Traditional",
                    },
                    {
                        image: `${url_prefix}imgs/traditional_meter.png`,
                        value: "economy_seven",
                        key: "economy_seven",
                        label: "Economy 7",
                    },
                ],

                house_spend: [
                    {
                        image_selected: `${url_prefix}imgs/low.png`,
                        image: `${url_prefix}imgs/low-disabled.png`,
                        value: "low",
                        label: "Low",
                    },
                    {
                        image_selected: `${url_prefix}imgs/medium.png`,
                        image: `${url_prefix}imgs/medium-disabled.png`,
                        value: "medium",
                        label: "Medium",
                    },
                    {
                        image_selected: `${url_prefix}imgs/high.png`,
                        image: `${url_prefix}imgs/high-disabled.png`,
                        value: "high",
                        label: "High",
                    },
                ],
                addresses: [],
            },
            stages,
            prev_stages: [],
            messages: stages.reduce((msgs, stage) => {
                msgs[stage] = [];
                return msgs;
            }, {}),
            lacks_smart_meter_support: [
                "British Gas",
                "EDF",
                "Npower",
                "ScottishPower",
                "SSE",
            ],
        };
    },

    methods: {
        async getAuthToken() {
            if (
                this.authToken &&
                this.tokenExpiry &&
                new Date() < new Date(this.tokenExpiry)
            ) {
                return this.authToken;
            }
            try {
                const response = await axios.get("https://azproxyuat.e.org/api/token");
                if (response.data && response.data.value) {
                    this.authToken = response.data.value;
                    this.tokenExpiry = response.data.expires;
                    return this.authToken;
                } else {
                    throw new Error("Invalid token response");
                }
            } catch (error) {
                console.error("Error fetching token:", error);
                throw error;
            }
        },

        parseAddressToArray(addressString) {
            const parts = addressString
                .split(",")
                .map((part) => part.trim())
                .filter((part) => part);
            if (parts.length === 0) return null;
            const addressArray = ["", "", "", "", "", "", "", ""];
            const postcode = parts[parts.length - 1];
            if (parts.length >= 2) {
                addressArray[5] = parts[parts.length - 2];
            }
            addressArray[0] = parts[0];
            if (parts.length > 3) {
                addressArray[1] = parts[1];
            }
            // addressArray[6] = "Scotland";
            addressArray[6] = postcode; // Store the postcode
            return addressArray;
        },

        isNotFoundResponse(addresses) {
            if (!addresses || addresses.length === 0) return true;
            if (
                addresses.length === 1 &&
                addresses[0].address === "Not found" &&
                addresses[0].id === "Not found"
            ) {
                return true;
            }
            return false;
        },

        formatUKPostcode(postcode) {
            const cleaned = postcode.replace(/\s+/g, "").toUpperCase();
            if (cleaned.length < 5 || cleaned.length > 7) {
                return null;
            }
            const outcode = cleaned.slice(0, -3);
            const incode = cleaned.slice(-3);
            return `${outcode} ${incode}`;
        },

        async getAddress() {
            const postcode = this.formatUKPostcode(this.form.post_code);
            try {
                const addressToken = await this.getAuthToken();
                const response = await axios.get(
                    `https://azproxyuat.e.org/api/address?token=${addressToken}&postcode=${encodeURIComponent(
                        postcode,
                    )}`,
                );
                if (this.isNotFoundResponse(response.data)) {
                    console.warn("No addresses found for postcode:", postcode);
                    this.options.addresses = [];
                    this.messages["postcode"] = [
                        "No addresses found for this postcode. Please check and try again.",
                    ];
                    return;
                }
                this.options.addresses = response.data
                    .map((item) => this.parseAddressToArray(item.address))
                    .filter((addr) => addr !== null);
            } catch (error) {
                this.messages["postcode"] = [
                    "Error searching for addresses. Please try again.",
                ];
                this.options.addresses = [];
            }
        },

        goBack() {
            this.form_stage = this.prev_stages.pop();
        },

        next(callback) {
            this.messages[this.form_stage] = [];
            callback();
            try {
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: "form_step",
                        stepName: this.form_stage,
                    });
                }
            } catch (e) {
                console.warn("dataLayer push failed:", e);
            }
        },
        submitPostcode() {
            if (this.postcode_valid) {
                this.getAddress();
                this.form_stage = "fuel_type";
                this.prev_stages.push("postcode");
            } else {
                this.messages["postcode"].push(
                    "You must enter a valid post code, E.G. LS12 1AB",
                );
            }
        },

        submitFuelType() {
            if (this.fuel_type_valid) {
                if (this.form.fuel_type.value === "Gas Only") {
                    this.$set(this.form, "meter_type", {
                        value: "traditional",
                    });
                    this.form_stage = "payment_type";
                } else {
                    this.form_stage = "meter_type";
                }
            } else {
                this.messages["fuel_type"].push(
                    "You must select a fuel type before continuing.",
                );
                return;
            }

            this.prev_stages.push("fuel_type");
        },

        submitMeterType() {
            if (this.meter_type_valid) {
                this.form_stage = "payment_type";
                this.prev_stages.push("meter_type");
            } else {
                if (!this.form.meter_type) {
                    this.messages["meter_type"].push(
                        "You must select a meter type before continuing.",
                    );
                    return;
                } else if (
                    this.form.meter_type.value === "economy_seven" &&
                    isNaN(this.form.economy7Percentage)
                )
                    this.messages["meter_type"].push(
                        "You must enter an Economy 7 percentage between 0 and 100",
                    );
            }
        },

        submitPaymentType() {
            if (this.form.prepayment_meter) {
                this.$set(
                    this.form,
                    "payment_type",
                    this.$store.state.payment_types.all.find(
                        (type) => type.name === "Prepayment Meter",
                    ),
                );

                if (this.form.isDualFuel) {
                    this.form_stage = "dual_supplier";
                } else if (
                    this.form.fuel_type?.value === "Dual" ||
                    this.form.fuel_type?.value === "Gas Only"
                ) {
                    this.form_stage = "gas_supplier";
                    this.$set(this.form, "gas_supplier", null);
                } else if (this.form.fuel_type?.value === "Electricity Only") {
                    this.form_stage = "electric_supplier";
                    this.$set(this.form, "electric_supplier", null);
                }
                this.prev_stages.push("payment_type");
            } else {
                this.$set(this.form, "payment_type", null);
            }
        },

        submitGasSupplier() {
            if (this.form.gas_supplier) {
                this.form_stage = "gas_tariff";
                this.prev_stages.push("gas_supplier");
                this.$set(this.form, "gas_tariff", null);
            } else {
                this.messages["gas_supplier"].push(
                    "You must select a supplier to continue.",
                );
            }
        },
        submitGasTariff() {
            if (this.form.gas_tariff) {
                if (this.form.fuel_type.value === "Dual") {
                    this.form_stage = "electric_supplier";
                } else {
                    this.form_stage = "usage";
                }

                this.prev_stages.push("gas_tariff");
                this.$set(this.form, "electricity_supplier", null);
            } else {
                this.messages["gas_tariff"].push(
                    "You must select a tariff to continue",
                );
            }
        },

        submitElectricSupplier() {
            if (this.form.electricity_supplier) {
                this.form_stage = "electric_tariff";
                this.prev_stages.push("electric_supplier");
                this.$set(this.form, "electricity_tariff", null);
            } else {
                this.messages["electric_supplier"].push(
                    "You must select a supplier to continue.",
                );
            }
        },
        submitElectricTariff() {
            if (this.form.electricity_tariff) {
                this.form_stage = "usage";
                this.prev_stages.push("electric_tariff");
            } else {
                this.messages["electric_tariff"].push(
                    "You must select a tariff to continue",
                );
            }
        },

        submitDualSupplier() {
            if (this.form.dual_supplier) {
                this.form_stage = "dual_tariff";
                this.prev_stages.push("dual_supplier");
            } else {
                this.messages["dual_supplier"].push(
                    "You must select a supplier to continue.",
                );
            }
        },
        submitDualTariff() {
            if (this.form.dual_tariff) {
                this.form_stage = "usage";
                this.prev_stages.push("dual_tariff");
            } else {
                this.messages["dual_tariff"].push(
                    "You must select a tariff to continue.",
                );
            }
        },
        submitUsage() {
            this.messages["usage"] = [];
            let condition = null;
            let type = null;
            if (this.form.fuel_type.value === "Gas Only") {
                condition = this.form.gasUsage;
                type = "gas";
            } else if (this.form.fuel_type.value === "Electricity Only") {
                condition = this.form.electricityUsage;
                type = "electricity";
            } else if (this.form.fuel_type.value === "Dual") {
                condition = this.form.electricityUsage && this.form.gasUsage;
                type = "gas and electricity";
            }

            if (
                condition ||
                (this.form.calculationType === "HouseSize" && this.form.usage)
            ) {
                this.$store.dispatch("getQuotes", {
                    ...this.form_submission,
                    newGasQuoteTariff: null,
                    newElectricityQuoteTariff: null,
                    gasQuoteTariff: null,
                    electricityQuoteTariff: null,
                });
                this.form_stage = "quote";
                this.prev_stages.push("usage");
            } else {
                this.messages["usage"].push(
                    `You must enter your ${type} ${this.form.calculationType === "Spend" ? "spend" : "usage"
                    }.`,
                );
            }
        },

        switchToE() {
            this.form_stage = "switch_to_us";
            this.prev_stages.push("quote");
        },

        OTPSent() {
            if (this.user_info_valid === true && !this.is_submitting) {
                const prefix = this.form.phone_number.substring(0, 3);
                const prefixZero = this.form.phone_number.substring(0, 1);
                if (prefixZero == "0")
                    this.form.phone_number = "+44" + this.form.phone_number.substring(1);
                if (prefixZero != "0" && prefix != "+44")
                    this.form.phone_number = "+44" + this.form.phone_number;
                this.$store
                    .dispatch("getTwiToken", this.form.phone_number)
                    .then((statustwi) => {
                        this.$store
                            .dispatch("sendOTP", {
                                telePhone: this.form.phone_number,
                                twiToken: statustwi,
                            })
                            .then((status) => {
                                if (!status) {
                                    this.messages["switch_to_us"].push(
                                        "Oops! something went wrong . Please try again after sometime",
                                    );
                                } else {
                                    this.form_stage = "verify_OTP";
                                    this.prev_stages.push("switch_to_us");
                                }
                            })
                            .catch(() => {
                                this.is_submitting = false;
                            });
                    });
            } else {
                let message = this.user_info_valid.filter(msg => msg !== "year_of_birth_error").map((msg) =>
                    msg
                        .replace("_", " ")
                        .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase())),
                );
                if (message.length > 0) {
                    this.messages["switch_to_us"].push(
                        `The following fields aren't valid: ${message.join(", ")}.`,
                    );
                }
                if (this.user_info_valid.includes("year_of_birth_error")) {
                    this.messages["switch_to_us"].push("Year of birth must be 1900 or later");
                }
            }
        },

        submitSwitch() {
            this.$store
                .dispatch("verifyOTP", {
                    phone_number: this.form.phone_number,
                    OTP: this.form.OTP,
                })
                .then((status) => {
                    if (status === true && !this.is_submitting) {
                        this.is_submitting = true;
                        this.$store
                            .dispatch("submitSwitch", this.form_submission)
                            .then((status) => {
                                this.is_submitting = false;
                                if (status === true) {
                                    this.$root.submitSwitchReferrer();
                                }
                            })
                            .catch(() => {
                                this.is_submitting = false;
                            });
                        this.form_stage = "done";
                        this.prev_stages.push("verify_OTP");
                    } else {
                        this.messages["verify_OTP"].push("Invalid OTP");
                    }
                });
        },

        // getAddress() {
        //     let postcode = this.form.post_code;
        //     axios.get(`https://api.getaddress.io/find/${postcode.replace(' ', '')}?api-key=u_ab7mznUkmDesNoQo0pYA25602`).then((response) => {
        //         this.options.addresses = response.data.addresses.map(address => address.split(', '))
        //     });
        // },

        changeDob(field) {
            let the_field = this.$refs[`dob_${field}`];
            switch (field) {
                case "day": {
                    if (the_field.value.length === 2) {
                        this.$refs.dob_month.focus();
                    }
                    break;
                }
                case "month": {
                    if (the_field.value.length === 2) {
                        this.$refs.dob_year.focus();
                    }
                    break;
                }
                case "year": {
                    const msg = "Year of birth must be 1900 or later";
                    const msgIndex = this.messages["switch_to_us"].indexOf(msg);
                    if (the_field.value.length === 4 && parseInt(the_field.value) < 1900) {
                        if (msgIndex === -1) {
                            this.messages["switch_to_us"].push(msg);
                        }
                    } else {
                        if (msgIndex !== -1) {
                            this.messages["switch_to_us"].splice(msgIndex, 1);
                        }
                    }
                    break;
                }
            }
        },
    },

    computed: {
        ...mapState({
            form: (state) => state.form,
            quotes: (state) => state.quotes || [],
        }),

        quote() {
            let type = this.form.fuel_type.value;
            let default_tariff = (tariff) => ({
                tariff: {},
                ...(tariff || {}),
                annualSpend: 0,
            });
            let gasQuoteTariff,
                newGasQuoteTariff,
                electricityQuoteTariff,
                newElectricityQuoteTariff;

            gasQuoteTariff = this.quotes.find((quote) => {
                return quote.tariff.supplier_id !== 7 && quote.type === "gas";
            });

            newGasQuoteTariff =
                this.quotes.find((quote) => {
                    return quote.tariff.supplier_id === 7 && quote.type === "gas";
                }) || default_tariff();

            if (!gasQuoteTariff) {
                gasQuoteTariff = default_tariff();
                newGasQuoteTariff = default_tariff(newGasQuoteTariff);
            }

            electricityQuoteTariff = this.quotes.find((quote) => {
                return quote.tariff.supplier_id !== 7 && quote.type === "electricity";
            });

            newElectricityQuoteTariff =
                this.quotes.find((quote) => {
                    return quote.tariff.supplier_id === 7 && quote.type === "electricity";
                }) || default_tariff();

            if (!electricityQuoteTariff) {
                electricityQuoteTariff = default_tariff();
                newElectricityQuoteTariff = default_tariff(newElectricityQuoteTariff);
            }

            return {
                type,
                gasQuoteTariff,
                newGasQuoteTariff,
                newElectricityQuoteTariff,
                electricityQuoteTariff,
            };
        },

        savings() {
            let quote = this.quote;
            let savedOnGas =
                quote.gasQuoteTariff.annualSpend - quote.newGasQuoteTariff.annualSpend;
            let savedOnPower =
                quote.electricityQuoteTariff.annualSpend -
                quote.newElectricityQuoteTariff.annualSpend;
            return savedOnGas + savedOnPower;
        },

        quote_positive() {
            if (!this.quote) return null;
            let quote = this.quote;
            let savedOnGas =
                quote.gasQuoteTariff.annualSpend - quote.newGasQuoteTariff.annualSpend;
            let savedOnPower =
                quote.electricityQuoteTariff.annualSpend -
                quote.newElectricityQuoteTariff.annualSpend;
            return savedOnGas + savedOnPower > 0 ? true : false;
        },

        form_index() {
            return this.stages.indexOf(this.form_stage);
        },

        featured_supplier_options() {
            return this.featured_suppliers.map((supplier) => ({
                ...supplier,
                icon_mode: "fit",
            }));
        },

        all_suppliers() {
            return this.$store.state.suppliers.all;
        },

        featured_suppliers() {
            return this.$store.getters.featured_suppliers;
        },

        payment_types() {
            return this.$store.state.payment_types.all;
        },

        gas_tariffs() {
            if (!this.$store.state.form.fuel_type) return [];
            if (!this.$store.state.form.payment_type) return [];
            if (!this.$store.state.form.gas_supplier) return [];

            return this.$store.state.gas_tariffs.all
                .filter((tariff) => tariff.gas === 1)
                .reduce((arr, tariff) => {
                    if (arr.findIndex((t) => t.name === tariff.name) < 0) {
                        arr.push(tariff);
                    }
                    return arr;
                }, []);
        },
        electric_tariffs() {
            if (!this.$store.state.form.fuel_type) return [];
            if (!this.$store.state.form.payment_type) return [];
            if (!this.$store.state.form.electricity_supplier) return [];

            return this.$store.state.electric_tariffs.all
                .filter((tariff) => tariff.electric == 1)
                .reduce((arr, tariff) => {
                    if (arr.findIndex((t) => t.name === tariff.name) < 0) {
                        arr.push(tariff);
                    }
                    return arr;
                }, []);
        },
        dual_tariffs() {
            if (!this.$store.state.form.fuel_type) return [];
            if (!this.$store.state.form.payment_type) return [];
            if (
                !(
                    this.$store.state.form.dual_supplier ||
                    this.$store.state.form.electric_supplier ||
                    this.$store.state.form.gas_supplier
                )
            )
                return [];

            return this.$store.state.dual_tariffs.all
                .filter((tariff) => tariff.gas === 1 && tariff.electric === 1)
                .reduce((arr, tariff) => {
                    if (arr.findIndex((t) => t.name === tariff.name) < 0) {
                        arr.push(tariff);
                    }
                    return arr;
                }, []);
        },

        has_electric_info() {
            return (
                this.form.fuel_type.value === "Electricity Only" ||
                (this.form.fuel_type.value === "Dual" && !this.form.isDualFuel)
            );
        },
        has_gas_info() {
            return (
                this.form.fuel_type.value === "Gas Only" ||
                (this.form.fuel_type.value === "Dual" && !this.form.isDualFuel)
            );
        },

        getIPaddress() {
            axios.get("https://api.ipify.org?format=json").then((response) => {
                ip = response.data.ip;
            });
        },

        form_submission() {
            let type = this.form.fuel_type.value;
            this.getIPaddress;
            return {
                ...this.quote,
                id: 1,
                postcode: this.form.post_code,
                type: this.form.fuel_type.value,
                isDualFuel: this.form.isDualFuel || false,
                isEconomy7:
                    this.form.meter_type?.value === "economy_seven" ? true : false,

                gasPaymentMethod: this.has_gas_info
                    ? this.form.payment_type
                    : defaults.payment_type,
                gasSupplier: this.has_gas_info
                    ? this.form.gas_supplier
                    : defaults.supplier,
                gasTariff: this.has_gas_info ? this.form.gas_tariff : defaults.tariff,

                electricityPaymentMethod: this.has_electric_info
                    ? this.form.payment_type
                    : defaults.payment_type,
                electricitySupplier: this.has_electric_info
                    ? this.form.electricity_supplier
                    : defaults.supplier,
                electricityTariff: this.has_electric_info
                    ? this.form.electricity_tariff
                    : defaults.tariff,

                dualPaymentMethod: this.form.isDualFuel
                    ? this.form.payment_type
                    : defaults.payment_type,
                dualSupplier: this.form.isDualFuel
                    ? this.form.dual_supplier
                    : defaults.supplier,
                dualTariff: this.form.isDualFuel
                    ? this.form.dual_tariff
                    : defaults.tariff,
                meterType:
                    this.form.meter_type.value === "economy_seven"
                        ? "K"
                        : this.form.meter_type.value === "traditional"
                            ? "K"
                            : "S1",
                region: this.$store.state.region,
                calculationType: this.form.calculationType,

                gasSpendPeriod:
                    this.form.calculationType === "Spend"
                        ? this.form.gas_usage_period
                        : "Year",
                gasUsagePeriod:
                    this.form.calculationType === "Spend"
                        ? "Year"
                        : this.form.gas_usage_period,

                electricitySpendPeriod:
                    this.form.calculationType === "Spend"
                        ? this.form.electricity_usage_period
                        : "Year",
                electricityUsagePeriod:
                    this.form.calculationType === "Spend"
                        ? "Year"
                        : this.form.electricity_usage_period,

                gasSpend:
                    this.form.calculationType === "Spend" ? this.form.gasUsage : null,
                gasUsage:
                    this.form.calculationType === "Spend" ? null : this.form.gasUsage,
                electricitySpend:
                    this.form.calculationType === "Spend"
                        ? this.form.electricityUsage
                        : null,
                electricityUsage:
                    this.form.calculationType === "Spend"
                        ? null
                        : this.form.electricityUsage,

                economy7Percentage: parseInt(this.form.economy7Percentage),
                forename: this.form.first_name || "",
                surname: this.form.last_name || "",
                AD1: this.form.selected_address?.[0] || "",
                AD2: this.form.selected_address?.[1] || "",
                email: this.form.email || "",
                telephone: this.form.phone_number,
                isElectricSmartMeter:
                    this.form.meter_type?.value === "S1" ? true : false,
                title: this.form.title,
                interestedElectricSmartMeter: this.form.interestedElectricSmartMeter,
                usage: this.form.usage || null,
                city: this.form.selected_address?.[5] || "",
                // county: this.form.selected_address?.[6] || "",
                mobile: "" || null,
                dob: this.form.age.day
                    ? `${this.form.age.day}/${this.form.age.month}/${this.form.age.year}`
                    : null,
                over18: this.is_over_18,
                minSpend: true,
                referrer: "https://azuat.e.org/sign-up",
                opt_out: !this.form.agrees_to_communication,
                number_of_days: this.form.number_of_days,
                consent_to_pay: this.form.consent_to_pay,
                nameOnAccount: "",
                bankName: "",
                sortCode: "",
                accountNumber: "",
                sortcodeError: "",
                accountNumberError: "",
                bankError: "",
                arePaymentTypeSame: true,
                gtmId: "TEST",
                ppmConsent: this.form.prepayment_Confirm,
                ipaddress: ip,
            };
        },

        both_fuels() {
            return this.form.fuel_type?.value === "Dual";
        },
        current_stage_index() {
            this.prev_stages.length + 1;
        },
        total_stages() { },
        form_progress() {
            return (
                (this.stages.indexOf(this.form_stage) / (this.stages.length - 2)) * 100
            );
        },

        is_over_18() {
            let diff = Math.abs(
                dayjs(
                    `${this.form.age.year}-${this.form.age.month}-${this.form.age.day}`,
                ).diff(dayjs(), "year"),
            );
            if (diff < 18) return false;
            else return true;
        },

        // VALIDATION
        postcode_valid() {
            return POSTCODE_REGEX.test(this.form.post_code);
        },

        fuel_type_valid() {
            if (!this.form.fuel_type) return false;
            return ["Dual", "Gas Only", "Electricity Only"].includes(
                this.form.fuel_type.value,
            );
        },

        meter_type_valid() {
            if (!this.form.meter_type) return false;
            if (
                this.form.meter_type.value === "economy_seven" &&
                isNaN(this.form.economy7Percentage)
            )
                return false;
            return true;
        },

        user_info_valid() {
            let invalid_fields = [];
            if (!this.form.title) {
                invalid_fields.push("title");
            }
            if (!this.form.first_name) {
                invalid_fields.push("first_name");
            }
            if (!this.form.last_name) {
                invalid_fields.push("last_name");
            }
            if (!this.form.phone_number) {
                invalid_fields.push("phone_number");
            }
            if (!this.form.email) {
                invalid_fields.push("email");
            }

            let dateStr = `${this.form.age.year}/${this.form.age.month}/${this.form.age.day}`;
            let date = dayjs(dateStr);

            if (!date.isValid()) {
                invalid_fields.push("age");
            } else if (parseInt(this.form.age.year) < 1900) {
                invalid_fields.push("year_of_birth_error");
            }
            if (!this.form.selected_address) {
                invalid_fields.push("address");
            }
            if (!this.form.prepayment_Confirm) {
                invalid_fields.push("Please select the payment confirmation");
            }
            if (
                this.form.agrees_to_communication === null ||
                this.form.agrees_to_communication === undefined
            ) {
                invalid_fields.push("Communication Preference");
            }

            if (invalid_fields.length) return invalid_fields;

            return true;
        },

        should_show_standing_charge() {
            return (
                this.quote?.newGasQuoteTariff?.tariff?.gas_standing_charge > 0 ||
                this.quote?.newElectricityQuoteTariff?.tariff
                    ?.electric_standing_charge > 0
            );
        },

        should_show_unit_rate() {
            return (
                this.quote?.newGasQuoteTariff?.tariff?.gas_kwh > 0 ||
                this.quote?.newElectricityQuoteTariff?.tariff?.electric_kwh > 0
            );
        },

        should_show_unit_rate_1() {
            return (
                this.quote?.newGasQuoteTariff?.tariff?.s1_gas_unit1_kwh > 0 ||
                this.quote?.newElectricityQuoteTariff?.tariff?.s1_electric_unit1_kwh > 0
            );
        },

        should_show_unit_rate_2() {
            return (
                this.quote?.newGasQuoteTariff?.tariff?.s1_gas_unit2_kwh > 0 ||
                this.quote?.newElectricityQuoteTariff?.tariff?.s1_electric_unit2_kwh > 0
            );
        },
    },
    watch: {
        'form.calculationType'(newVal) {
            if (newVal === 'KWH') {
                this.$set(this.form, 'electricity_usage_period', 'Year');
            } else {
                this.$set(this.form, 'electricity_usage_period', "Week");
            }
        },
        'form.calculationType'(newVal) {
            if (newVal === 'KWH') {
                this.$set(this.form, 'electricity_usage_period', 'Year');
                this.$set(this.form, 'gas_usage_period', 'Year');
            } else {
                this.$set(this.form, 'electricity_usage_period', "Week");
                this.$set(this.form, 'gas_usage_period', "Week");
            }
        },
        form_stage() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
    },
};
</script>
<style lang="scss" scoped>
.form {
    max-width: 900px;
    font-size: 16px;
    min-height: 700px;

    @media screen and (max-width: 800px) {
        max-width: 600px;
    }

    @media screen and (max-width: 600px) {
        max-width: 300px;
    }
}

.form>.cbox-wrapper {
    @apply mx-8;
}

input[type="text"] {
    @apply w-full border-2 border-black p-4;
    font-size: 16px;

    &.currency {
        padding-left: calc(1rem + 8px);
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="500" height="500"><g transform="translate(0,-552.36218)" id="layer1"><path d="m 301.29681,788.55573 0,32.9956 -81.5918,0 c 5.99351,17.19988 8.99033,31.20133 8.99048,42.0044 l 0,0.60424 c -1.5e-4,21.9972 -11.59682,46.79576 -34.79004,74.39576 -9.20421,11.20611 -21.20371,24.20659 -35.99854,39.00146 23.5961,-15.60053 47.99182,-23.40082 73.18726,-23.40088 14.0013,6e-5 30.40143,3.00299 49.20044,9.00879 21.99685,6.39653 37.79883,9.59477 47.406,9.59473 16.00316,4e-5 32.60471,-5.99971 49.80469,-17.99927 l 25.19531,39.60571 c -22.80303,22.39993 -49.00539,33.59983 -78.60717,33.59983 -17.60277,0 -42.99947,-5.603 -76.19019,-16.8091 l -11.40747,-4.1931 c -13.19594,-4.4067 -26.39783,-6.6101 -39.60571,-6.6101 -21.1915,0 -42.98713,7.4036 -65.38696,22.2107 l -28.8025,-39.6057 c 40.79584,-35.20501 64.79484,-65.40518 71.99708,-90.60058 2.00185,-7.20202 3.00283,-14.80089 3.00292,-22.79664 -9e-5,1.5e-4 -9e-5,-0.20127 0,-0.60424 l 0,-0.60425 c -9e-5,-15.19759 -4.80355,-30.79816 -14.4104,-46.80176 l -65.991206,0 0,-32.9956 48.596196,0 c -18.39605,-30.39526 -28.59501,-52.19089 -30.59693,-65.38697 -0.8057,-3.60078 -1.20853,-7.60468 -1.20849,-12.01172 l 0,-6.00586 c -4e-5,-39.59926 16.00336,-71.99669 48.01025,-97.19238 26.4037,-20.80035 57.80015,-31.20073 94.18945,-31.20117 l 0.60425,0 c 45.20242,4.4e-4 80.20604,14.80145 105.01099,44.40308 l 10.19897,14.99633 c 10.79072,18.39636 16.38763,44.99545 16.79077,79.79737 l -52.7893,0 c -1.59938,-61.59632 -28.80272,-92.39463 -81.61011,-92.39502 -35.59584,3.9e-4 -60.59582,12.59803 -75,37.79297 -6.39658,11.20639 -9.59482,24.01156 -9.59473,38.41552 l 0,0.58594 c -9e-5,15.21026 4.40054,31.21366 13.20191,48.01025 l 0,0.60425 0.60424,0.58594 c 1.59902,3.60134 3.79629,8.00197 6.5918,13.2019 l 5.40161,9.00879 9.59473,16.79078 z" /></g></svg>');
        background-size: 16px 16px;
        background-repeat: no-repeat;
        background-position: 5px center;
    }

    &.kwh {
        padding-right: calc(1rem + 35px);
    }
}

select {
    @apply w-full border-2 border-black hover:border-orange p-4 mb-8 relative font-bold;
    appearance: none;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 255 255"> <polygon points="0,0 127.5,127.5 255,0"/> </svg>');
    background-position: calc(100% - 1rem) 25px;
    background-repeat: no-repeat;
    background-size: 15px 15px;
    background-color: white;
    font-size: 16px;
    padding-right: 30px;

    &.small {
        background-position: calc(100% - 0.5rem) 10px;
        @apply p-1;
    }
}

button {
    @apply py-4 mx-4 bg-energy-blue text-white mt-8;
}

.text-button {
    @apply sm:w-3/5 md:w-2/5 border-2 cursor-pointer hover:border-orange border-black p-3 relative flex justify-between font-bold items-center my-4;
    width: calc(100% - 4rem);

    &.selected {
        @apply border-orange;
    }

    height: 61px;
}

label.input {
    width: calc(100% - 4rem);
    text-align: left;
    @apply sm:w-3/5 md:w-2/5 font-bold;
}

select,
button,
.text-button,
input[type="text"],
input[type="range"] {
    width: calc(100% - 4rem);
    height: 61px;
    @apply sm:w-3/5 md:w-2/5 font-bold;

    &.small {
        height: 32px;
        width: auto;
    }
}

.progress-bar {
    width: calc(100% - 4rem);
    @apply mx-8;
}

.radio-group label {
    position: relative;
    padding-left: 40px;
    min-height: 25px;
    margin-bottom: 16px;
    @apply font-bold;
    font-size: 16px;
    cursor: pointer;

    &:hover::before {
        @apply border-orange border-2;
        background-color: #eeeeee;
    }

    &::before {
        background-color: #eee;
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        content: " ";
        height: 25px;
        width: 25px;
        border-radius: 12.5px;
    }
}

.radio-group input {
    opacity: 0;
}

.radio-group input:checked+label::before {
    @apply bg-orange;
}

#modal-target {
    padding: 10%;
    pointer-events: none;

    &.open {
        background-color: #0001;
        pointer-events: auto;
    }
}

.benefit {
    @apply w-full text-center;

    @screen xs {
        @apply w-auto;
        flex: 2;
    }

    font-size: 14px;
    whitespace: nowrap;
    text-overflow: clip;
    overflow: hidden;
    min-width: 40%;

    &:nth-child(odd) {
        flex: 3;
        min-width: 40%;
    }
}

table {
    border: 1px solid;
    @apply w-full;

    td,
    th {
        border: 1px solid;
        padding: 2px;
    }
}

.progress-bar {
    // border: 1px solid pink;
}

@-webkit-keyframes progress-bar-stripes {
    from {
        background-position: 40px 0;
    }

    to {
        background-position: 0 0;
    }
}

@-o-keyframes progress-bar-stripes {
    from {
        background-position: 40px 0;
    }

    to {
        background-position: 0 0;
    }
}

@keyframes progress-bar-stripes {
    from {
        background-position: 40px 0;
    }

    to {
        background-position: 0 0;
    }
}

.actual-progress {
    transition: width 0.5s ease-out;
    -webkit-animation: progress-bar-stripes 2s linear infinite;
    -o-animation: progress-bar-stripes 2s linear infinite;
    animation: progress-bar-stripes 2s linear infinite;
    background-image: -webkit-linear-gradient(45deg,
            rgba(255, 255, 255, 0.15) 25%,
            transparent 25%,
            transparent 50%,
            rgba(255, 255, 255, 0.15) 50%,
            rgba(255, 255, 255, 0.15) 75%,
            transparent 75%,
            transparent);
    background-image: -o-linear-gradient(45deg,
            rgba(255, 255, 255, 0.15) 25%,
            transparent 25%,
            transparent 50%,
            rgba(255, 255, 255, 0.15) 50%,
            rgba(255, 255, 255, 0.15) 75%,
            transparent 75%,
            transparent);
    background-image: linear-gradient(45deg,
            rgba(255, 255, 255, 0.15) 25%,
            transparent 25%,
            transparent 50%,
            rgba(255, 255, 255, 0.15) 50%,
            rgba(255, 255, 255, 0.15) 75%,
            transparent 75%,
            transparent);
    -webkit-background-size: 40px 40px;
    background-size: 40px 40px;
    background-color: #f38c42 !important;
}

.date-of-birth {
    padding-top: 2px;
    padding-bottom: 2px;

    input {
        border: none;
        height: 25px;
        padding-left: 0px;
        padding-right: 0px;
        padding-top: 0px;
        padding-bottom: 0px;
        text-align: center;
    }

    position: relative;

    &::before {
        @apply font-bold;
        content: "Date of Birth";
        position: absolute;
        bottom: 100%;

        z-index: 10;
        left: 0;
        font-size: 14px;
    }
}

select.underline-input {
    &.mb-0 {
        margin-bottom: 0;
    }

    height: 35px;
    padding-top: 0;
    padding-bottom: 8px;
    border-width: 0 0 2px 0;
    background-position: calc(100% - 5px) 10px !important;
    padding-left: 0px;

    option {
        padding-left: 0px;
        left: -5px;
    }
}

input.underline-input {
    border-width: 0 0 2px 0;
    width: 100%;
    padding-bottom: 18px;
    padding-top: 18px;
    padding-left: 4px;
    height: 45px;

    &::placeholder {
        @apply font-semibold;
        color: black;
    }

    &.phone-icon {
        background: url("imgs/phone.png");
        background-repeat: no-repeat;
        background-size: 33px 33px;
        background-position: 10px left;
        padding-left: 40px;
    }

    &.email-icon {
        background: url("imgs/email.png");
        background-repeat: no-repeat;
        background-size: 39px 39px;
        background-position: bottom left;
        padding-left: 40px;
    }
}

.text-normal {
    font-weight: 400 !important;
    font-size: 14px !important;

    @screen sm {
        font-size: 16px !important;
    }
}

.select-address {
    @apply w-full;
}

.consent-to-pay {
    font-size: 11px !important;
    width: 551%;
    position: absolute;
    margin: 8%;

    @media screen and (max-width: 800px) {
        width: 443%;
    }
}

.dd-number-of-days {
    width: 61%;
    margin-right: 34%;

    @media screen and (max-width: 800px) {
        width: 66%;
        margin-right: 20%;
    }

    @media screen and (max-width: 600px) {
        width: 130%;
        margin-right: 25%;
    }
}

@media (min-width: 991px) {
    .benefits {
        width: 800px;
    }
}
</style>
